// client/src/coms/views/canvas_parts/CanvasReplayModal.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CanvasStageSurface from './CanvasStageSurface';
import { DEFAULT_CALLIGRAPHY_SETTINGS } from './canvas_room_view_parts/calligraphyBrushUtils';
import {
  DEFAULT_LIQUIFY_MODE,
  DEFAULT_LIQUIFY_SETTINGS,
} from './canvas_room_view_parts/liquifyUtils';

const REPLAY_SPEED_OPTIONS = [0.5, 1, 2, 4, 8, 16, 32, 64, 128, 256];
const EVENTS_PER_SECOND = 60;
const MAX_EVENTS_PER_FRAME = 4096;
const SEEK_EVENTS_PER_FRAME = 1000;

function clampNumber(value, min, max, fallback = min) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(min, Math.min(max, number));
}

function getEventLayerId(event) {
  return String(
    event?.layerId
    || event?.state?.layerId
    || event?.targetLayerId
    || ''
  ).trim();
}

function getEventOwnerName(event) {
  return String(
    event?.ownerName
    || event?.senderName
    || event?.userName
    || event?.displayName
    || ''
  ).trim();
}

function sortReplayEvents(events) {
  return (Array.isArray(events) ? events : [])
    .filter((event) => event && typeof event === 'object')
    .map((event, index) => ({ event, index }))
    .sort((left, right) => {
      const leftSequence = Number(left.event.replaySequence);
      const rightSequence = Number(right.event.replaySequence);
      if (Number.isFinite(leftSequence) && Number.isFinite(rightSequence) && leftSequence !== rightSequence) {
        return leftSequence - rightSequence;
      }
      const leftTime = Number(left.event.loggedAt || left.event.timestamp || left.event.seq || 0);
      const rightTime = Number(right.event.loggedAt || right.event.timestamp || right.event.seq || 0);
      if (leftTime !== rightTime) return leftTime - rightTime;
      return left.index - right.index;
    })
    .map((entry) => entry.event);
}

function ReplayButton({ children, onClick, disabled, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 76,
        height: 34,
        borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.14)',
        background: disabled ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.08)',
        color: disabled ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.94)',
        fontSize: 12,
        fontWeight: 900,
        cursor: disabled ? 'default' : 'pointer',
        padding: '0 12px',
      }}
    >
      {children}
    </button>
  );
}

export default function CanvasReplayModal({
  open,
  width,
  height,
  layers,
  events,
  onClose,
}) {
  const stageRef = useRef(null);
  const cursorRef = useRef(0);
  const frameRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const eventBudgetRef = useRef(0);
  const seekTokenRef = useRef(0);
  const autoPlayPendingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(8);
  const [seeking, setSeeking] = useState(false);

  const replayLayers = useMemo(() => {
    const safeLayers = (Array.isArray(layers) ? layers : [])
      .filter((layer) => layer && String(layer.id || '').trim())
      .map((layer, index) => ({
        ...layer,
        id: String(layer.id),
        name: String(layer.name || `Layer ${index + 1}`),
        visible: true,
        opacity: clampNumber(layer.opacity == null ? 1 : layer.opacity, 0, 1, 1),
      }));
    return safeLayers.length
      ? safeLayers
      : [{ id: 'replay-layer-1', name: 'Layer 1', visible: true, opacity: 1 }];
  }, [layers]);

  const replayEvents = useMemo(() => sortReplayEvents(events), [events]);
  const eventCount = replayEvents.length;
  const safeCursor = Math.max(0, Math.min(eventCount, Number(cursor) || 0));
  const progressPercent = eventCount > 0 ? (safeCursor / eventCount) * 100 : 0;
  const currentEvent = safeCursor > 0 ? replayEvents[Math.min(eventCount - 1, safeCursor - 1)] : null;
  const currentLayerId = getEventLayerId(currentEvent);
  const currentLayer = replayLayers.find((layer) => String(layer.id) === currentLayerId) || null;
  const currentOwnerName = getEventOwnerName(currentEvent) || String(currentLayer?.ownerName || '').trim();

  const stopAnimationFrame = useCallback(() => {
    if (typeof window !== 'undefined' && frameRef.current) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    }
  }, []);

  const resetPlaybackCanvas = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return false;
    const firstLayerId = String(replayLayers[0]?.id || 'replay-layer-1');
    stage.setLayers(replayLayers, firstLayerId);
    replayLayers.forEach((layer) => {
      stage.clearLayer(String(layer.id));
    });
    cursorRef.current = 0;
    setCursor(0);
    lastFrameTimeRef.current = 0;
    eventBudgetRef.current = 0;
    return true;
  }, [replayLayers]);

  const applyEventAtIndex = useCallback(async (index) => {
    const stage = stageRef.current;
    const event = replayEvents[index];
    if (!stage || !event) return false;
    const result = stage.applyRemoteLiveEvent(event);
    if (result && typeof result.then === 'function') await result;
    return true;
  }, [replayEvents]);

  const rebuildToCursor = useCallback((targetCursor, playAfter = false) => {
    const target = Math.max(0, Math.min(eventCount, Number(targetCursor) || 0));
    const token = seekTokenRef.current + 1;
    seekTokenRef.current = token;
    stopAnimationFrame();
    setPlaying(false);
    setSeeking(true);
    if (!resetPlaybackCanvas()) {
      setSeeking(false);
      return;
    }

    const applyChunk = async () => {
      if (seekTokenRef.current !== token) return;
      const startIndex = cursorRef.current;
      const endIndex = Math.min(target, startIndex + SEEK_EVENTS_PER_FRAME);
      for (let index = startIndex; index < endIndex; index += 1) {
        await applyEventAtIndex(index);
        if (seekTokenRef.current !== token) return;
      }
      cursorRef.current = endIndex;
      setCursor(endIndex);

      if (endIndex < target) {
        frameRef.current = window.requestAnimationFrame(applyChunk);
        return;
      }

      frameRef.current = 0;
      setSeeking(false);
      lastFrameTimeRef.current = 0;
      eventBudgetRef.current = 0;
      if (playAfter && target < eventCount) setPlaying(true);
    };

    frameRef.current = window.requestAnimationFrame(applyChunk);
  }, [applyEventAtIndex, eventCount, resetPlaybackCanvas, stopAnimationFrame]);

  const handleReady = useCallback(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!open) {
      seekTokenRef.current += 1;
      stopAnimationFrame();
      setPlaying(false);
      setSeeking(false);
      setReady(false);
      cursorRef.current = 0;
      setCursor(0);
      return;
    }
    autoPlayPendingRef.current = true;
  }, [open, stopAnimationFrame]);

  useEffect(() => {
    if (!open || !ready) return;
    const shouldAutoPlay = autoPlayPendingRef.current;
    autoPlayPendingRef.current = false;
    rebuildToCursor(0, shouldAutoPlay && eventCount > 0);
  }, [eventCount, open, ready, rebuildToCursor, replayEvents, replayLayers]);

  useEffect(() => {
    if (!open || !ready || !playing || seeking || cursorRef.current >= eventCount) return undefined;

    let cancelled = false;
    const drawFrame = async (time) => {
      if (cancelled) return;
      const previousTime = lastFrameTimeRef.current || time;
      const elapsed = Math.max(0, Math.min(100, time - previousTime));
      lastFrameTimeRef.current = time;
      eventBudgetRef.current += (elapsed / 1000) * EVENTS_PER_SECOND * speed;

      let eventsToApply = Math.floor(eventBudgetRef.current);
      if (eventsToApply < 1) {
        frameRef.current = window.requestAnimationFrame(drawFrame);
        return;
      }

      eventsToApply = Math.min(eventsToApply, MAX_EVENTS_PER_FRAME);
      eventBudgetRef.current -= eventsToApply;
      const startIndex = cursorRef.current;
      const endIndex = Math.min(eventCount, startIndex + eventsToApply);

      for (let index = startIndex; index < endIndex; index += 1) {
        await applyEventAtIndex(index);
        if (cancelled) return;
      }

      cursorRef.current = endIndex;
      setCursor(endIndex);

      if (endIndex >= eventCount) {
        frameRef.current = 0;
        setPlaying(false);
        lastFrameTimeRef.current = 0;
        eventBudgetRef.current = 0;
        return;
      }

      frameRef.current = window.requestAnimationFrame(drawFrame);
    };

    frameRef.current = window.requestAnimationFrame(drawFrame);
    return () => {
      cancelled = true;
      stopAnimationFrame();
    };
  }, [applyEventAtIndex, eventCount, open, playing, ready, seeking, speed, stopAnimationFrame]);

  useEffect(() => () => {
    seekTokenRef.current += 1;
    stopAnimationFrame();
  }, [stopAnimationFrame]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && typeof onClose === 'function') onClose();
      if (event.code === 'Space' && event.target === document.body) {
        event.preventDefault();
        if (eventCount > 0 && !seeking) {
          if (cursorRef.current >= eventCount) rebuildToCursor(0, true);
          else setPlaying((value) => !value);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [eventCount, onClose, open, rebuildToCursor, seeking]);

  if (!open) return null;

  const canvasAspectRatio = Math.max(0.1, Number(width) || 1) / Math.max(0.1, Number(height) || 1);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Canvas playback"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        background: 'rgba(8,10,14,0.88)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: 'min(1500px, 100%)',
          height: 'min(920px, 100%)',
          minHeight: 0,
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.16)',
          background: '#252525',
          boxShadow: '0 30px 100px rgba(0,0,0,0.58)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            height: 48,
            flex: '0 0 48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '0 14px',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            background: '#303030',
            color: 'rgba(255,255,255,0.94)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 950 }}>Drawing playback</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.58)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentOwnerName ? `${currentOwnerName} · ` : ''}{currentLayer ? currentLayer.name : 'Step zero'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close playback"
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.07)',
              color: 'rgba(255,255,255,0.94)',
              cursor: 'pointer',
              fontSize: 19,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            padding: 18,
            boxSizing: 'border-box',
            background: '#777777',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `min(100%, calc((100vh - 190px) * ${canvasAspectRatio}))`,
              aspectRatio: `${Math.max(1, Number(width) || 1)} / ${Math.max(1, Number(height) || 1)}`,
              maxHeight: '100%',
              background: '#ffffff',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.44), 0 18px 60px rgba(0,0,0,0.35)',
            }}
          >
            <CanvasStageSurface
              ref={stageRef}
              width={Math.max(1, Number(width) || 1)}
              height={Math.max(1, Number(height) || 1)}
              strokeColor="#111111"
              brushSize={1}
              brushSoftness={0}
              jitterAmount={0}
              opacity={1}
              activeTool="replay"
              bucketExpansion={0}
              activeBrushPreset="pen"
              activeImportedBrush={null}
              pencilOrientation={0}
              pencilGrade={0}
              calligraphySettings={{ ...DEFAULT_CALLIGRAPHY_SETTINGS }}
              liquifyMode={DEFAULT_LIQUIFY_MODE}
              liquifySettings={{ ...DEFAULT_LIQUIFY_SETTINGS }}
              airbrushDistance={100}
              airbrushPaintFlow={20}
              airbrushPressurePaintFlowEnabled={false}
              airbrushAirPressure={5}
              airbrushNeedleLimit={100}
              onReady={handleReady}
              onStrokeComplete={null}
              onPixelsChanged={null}
              onLocalLiveEvent={null}
              layers={replayLayers}
              activeLayerId={String(replayLayers[0]?.id || 'replay-layer-1')}
              canEditActiveLayer={false}
              viewPan={{ x: 0, y: 0 }}
              onViewPanChange={null}
            />
          </div>
        </div>

        <div
          style={{
            flex: '0 0 auto',
            padding: '12px 14px 14px',
            borderTop: '1px solid rgba(255,255,255,0.10)',
            background: '#303030',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <input
            type="range"
            min="0"
            max={eventCount}
            step="1"
            value={safeCursor}
            disabled={!ready || eventCount <= 0 || seeking}
            onChange={(event) => rebuildToCursor(Number(event.target.value), false)}
            style={{ width: '100%', margin: 0, accentColor: 'rgba(147,197,253,0.96)' }}
          />
          <div style={{ height: 5, borderRadius: 999, background: 'rgba(0,0,0,0.34)', overflow: 'hidden' }}>
            <div style={{ width: `${progressPercent}%`, height: '100%', background: 'rgba(147,197,253,0.88)' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <ReplayButton
                onClick={() => {
                  if (cursorRef.current >= eventCount) rebuildToCursor(0, true);
                  else setPlaying((value) => !value);
                }}
                disabled={!ready || eventCount <= 0 || seeking}
                title={playing ? 'Pause playback' : 'Play drawing'}
              >
                {playing ? 'Pause' : 'Play'}
              </ReplayButton>
              <ReplayButton
                onClick={() => rebuildToCursor(0, false)}
                disabled={!ready || eventCount <= 0 || seeking}
                title="Return to step zero"
              >
                Rewind
              </ReplayButton>
              <ReplayButton
                onClick={() => rebuildToCursor(Math.max(0, cursorRef.current - 1), false)}
                disabled={!ready || safeCursor <= 0 || seeking}
                title="Step back one event"
              >
                Step −
              </ReplayButton>
              <ReplayButton
                onClick={async () => {
                  if (cursorRef.current >= eventCount || seeking) return;
                  setPlaying(false);
                  await applyEventAtIndex(cursorRef.current);
                  cursorRef.current += 1;
                  setCursor(cursorRef.current);
                }}
                disabled={!ready || safeCursor >= eventCount || seeking}
                title="Step forward one event"
              >
                Step +
              </ReplayButton>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.72)', fontSize: 12, fontWeight: 850 }}>
              <span>{seeking ? 'Building…' : `${safeCursor} / ${eventCount} events`}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Speed
                <select
                  value={String(speed)}
                  onChange={(event) => setSpeed(clampNumber(event.target.value, 0.5, 256, 1))}
                  style={{
                    height: 31,
                    borderRadius: 5,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(0,0,0,0.26)',
                    color: 'rgba(255,255,255,0.94)',
                    padding: '0 8px',
                    fontSize: 12,
                    fontWeight: 900,
                  }}
                >
                  {REPLAY_SPEED_OPTIONS.map((option) => (
                    <option key={option} value={String(option)}>{option}×</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
